// Buying a Service articles for the Help Center.

const buying = {
  slug: 'buying',
  label: 'Buying a Service',
  icon: 'PiShoppingBag',
  description: 'How to find someone to hire, agree on the work, and pay safely.',
  articles: [
    {
      slug: 'finding-a-service',
      title: 'Finding a service to buy',
      summary: 'Browse by category, filter by school or price, and compare packages.',
      body: [
        {
          type: 'p',
          text: 'Open Hire Talent to browse everything students are offering, sorted into categories like design, writing, tutoring, and more. You can filter by university, price range, and category using the panel on the side of the page.',
        },
        {
          type: 'image',
          src: '/images/help/buying/hire-talent-browse.png', width: 1200, height: 615,
          alt: 'The Hire Talent browse page, showing listing cards with category and university filters',
          caption: 'Browsing Hire Talent',
        },
        {
          type: 'p',
          text: 'Each listing shows one or more packages, so you can see pricing, what is included, and delivery time before you talk to anyone. Open a listing to see the full description, the seller profile, reviews from past clients, and any FAQs the seller has already answered.',
        },
        {
          type: 'image',
          src: '/images/help/buying/listing-detail.png', width: 1200, height: 615,
          alt: 'A listing detail page, showing package tiers, pricing, and the seller profile',
          caption: 'A listing’s packages and pricing',
        },
        {
          type: 'note',
          text: 'You do not need to create an order to look around. Browsing, opening listings, and reading reviews are all free.',
        },
      ],
    },
    {
      slug: 'describing-what-you-need',
      title: 'Describing what you need instead of searching',
      summary: 'Type a sentence about your project, and Hire Talent finds listings that match.',
      body: [
        {
          type: 'p',
          text: 'If you do not know the exact category or keywords for what you are after, you do not have to guess. On Hire Talent, describe what you need in plain language, for example a logo for my coffee shop, and it translates that into a real search: category, price range, and a few keywords pulled from what you typed.',
        },
        {
          type: 'p',
          text: 'This works whether you are signed in or not. It is meant to lower the barrier for a first visit, not replace browsing by category if you already know what you are looking for.',
        },
        {
          type: 'note',
          text: 'It only ever adjusts the search filters. It never picks a service or a seller for you.',
        },
      ],
    },
    {
      slug: 'setting-up-your-business-profile',
      title: 'Setting up your business profile',
      summary: 'A short wizard the first time you post a job or request a service, so freelancers know who you are.',
      body: [
        {
          type: 'p',
          text: 'Before you post your first job or request a service, you will be asked to fill out a short business profile: who you are or what organization you represent, an industry, a bit about what you do, and your city. Freelancers see this before deciding whether to spend time on a proposal, so it is worth filling in for real rather than clicking through it.',
        },
        {
          type: 'p',
          text: 'A logo and website are optional but help. A posting with a logo on it reads as a real client rather than a throwaway account.',
        },
        {
          type: 'note',
          text: 'You can edit any of this later from your profile. It only walks you through it once, the first time you need it.',
        },
      ],
    },
    {
      slug: 'messaging-a-seller-before-you-buy',
      title: 'Messaging a seller before you buy',
      summary: 'Describe what you need first. This is required before you can request a service agreement.',
      body: [
        {
          type: 'p',
          text: 'Before you can request a service agreement on a listing, you need to send the seller a message describing what you actually need. This is not optional. The button to request an agreement stays locked until you have sent at least one message in that conversation.',
        },
        {
          type: 'p',
          text: 'This step exists so both sides know what they are agreeing to before any paperwork or payment happens. A good message includes what you need, any deadlines, and anything specific about your project the seller should know.',
        },
        {
          type: 'steps',
          items: [
            'Open the listing and select Message the seller.',
            'Describe what you need in plain language.',
            'Once you have sent a message, the package picker in the conversation unlocks.',
          ],
        },
      ],
    },
    {
      slug: 'understanding-packages',
      title: 'Understanding packages',
      summary: 'Most listings offer a few tiers with different prices and deliverables.',
      body: [
        {
          type: 'p',
          text: 'Sellers can offer up to three packages on a listing, usually named Basic, Standard, and Premium. Each one lists its own price, what is included, how long delivery takes, and how many rounds of revisions come with it.',
        },
        {
          type: 'p',
          text: 'Use these to get a sense of price and scope, but you do not pick one yourself. Message the seller and describe what you actually need, and they will propose the package (or a custom scope) that fits when they send the service agreement.',
        },
      ],
    },
    {
      slug: 'the-service-agreement-explained',
      title: 'The service agreement explained',
      summary: 'A short written agreement both sides sign before any money moves.',
      body: [
        {
          type: 'p',
          text: 'A service agreement is a short document that spells out what is being delivered, how many revisions are included, who owns the finished work, and any other terms the seller has set. It is not legal paperwork you need a lawyer for. Think of it as a clear, written version of what you already agreed on in chat.',
        },
        {
          type: 'p',
          text: 'You message the seller about what you need, and once they are ready to formalize it, they send you the agreement, built from the standing terms they set when they opened the listing, for whichever package they are proposing. You review it, sign with your typed name, and pay in the same step. Nothing is charged until you sign.',
        },
        {
          type: 'steps',
          items: [
            'Message the seller about what you need.',
            'The seller sends the service agreement for the package they are proposing.',
            'You review it, sign with your name, and pay, all in one step.',
            'Your payment is held in escrow until you approve the finished work.',
          ],
        },
        {
          type: 'note',
          text: 'If you decline the agreement, nothing is charged. You can message the seller to work out a better fit, or look elsewhere.',
        },
      ],
    },
    {
      slug: 'paying-for-an-order',
      title: 'Paying for an order',
      summary: 'Your payment is held safely until you approve the finished work.',
      body: [
        {
          type: 'p',
          text: 'Once both you and the seller have signed the service agreement, a Pay button appears in that conversation. Your total includes the package price plus a small service fee, shown clearly before you pay. Your card is charged through Stripe, the same payment processor Shopify and Instacart use for their own checkouts.',
        },
        {
          type: 'p',
          text: 'Your payment is not sent to the seller right away. It is held until you approve the delivered work, so you are never paying for something you have not seen yet.',
        },
        {
          type: 'note',
          text: 'If the item you bought needs to be shipped, you will be asked for a delivery address as part of this step, before you pay.',
        },
      ],
    },
    {
      slug: 'tracking-your-order',
      title: 'Tracking your order',
      summary: 'See the status of everything you have bought in one place.',
      body: [
        {
          type: 'p',
          text: 'Once you have paid, your order moves to an active state and you can track it from the Orders page or from the conversation with the seller. Both show the current stage, any files the seller has delivered, and how many revisions you have left.',
        },
        {
          type: 'p',
          text: 'If the seller has listed the steps they follow for this kind of project, you will see that checklist too, so you know roughly what stage the work is at even before anything is delivered.',
        },
        {
          type: 'p',
          text: 'For a physical item, the order shows the carrier and a tracking link once it is posted. You have 10 days to confirm it arrived before payment releases. If it is running late, use "Hasn\'t arrived yet" on the order to add 7 more days (once). If the seller sent it without tracking and it still has not arrived 7 days after posting, open a review with "Never arrived" and you are refunded in full automatically.',
        },
      ],
    },
    {
      slug: 'approving-work-or-asking-for-changes',
      title: 'Approving work or asking for changes',
      summary: 'Review what was delivered, then approve it or request a revision.',
      body: [
        {
          type: 'p',
          text: 'When the seller delivers the work, you will get a notification and see it in your conversation. From there you have two options. Approve the delivery, which releases payment to the seller. Or request a revision, if the package you bought includes any and you have not used them all yet.',
        },
        {
          type: 'p',
          text: 'You have 7 days after a delivery to approve it or request a revision (10 days for a shipped item). We remind you with 3 days left and again on the last day. If you still do not respond, the order automatically completes and payment is released. This keeps sellers from being stuck waiting forever, so it is worth reviewing delivered work promptly.',
        },
      ],
    },
    {
      slug: 'proposing-a-change-to-an-active-order',
      title: 'Proposing a change to an active order',
      summary: 'Ask the seller to agree to a different scope, timeline, or revision count mid-order.',
      body: [
        {
          type: 'p',
          text: 'If what you need changes after an order is already active, either side can propose a change from that order: a new price, delivery time, deliverables or revisions, plus a note. The other person accepts or declines it, and nothing changes until they accept. If the price goes up, you pay the difference (plus the service fee) before it takes effect; if it goes down, the difference is refunded to your card.',
        },
      ],
    },
    {
      slug: 'if-you-disagree-about-a-delivery',
      title: 'If you disagree about a delivery',
      summary: 'Open a review if you and the seller cannot agree the work meets what was promised.',
      body: [
        {
          type: 'p',
          text: 'If delivered work does not meet what you agreed to and a revision or a message to the seller has not resolved it, you can open a review on the order instead of letting it sit. This pauses the order so payment cannot release automatically while it is being sorted out, and gives both of you a place to explain your side.',
        },
        {
          type: 'p',
          text: 'You can open one once the seller has had a real chance to deliver: either something has been handed over or posted, or the delivery window on the package has run out. Before that there is nothing to review yet, and you have a better option anyway, which is to cancel the order outright and be refunded in full.',
        },
        {
          type: 'p',
          text: 'The seller then has three days to reply with their side and attach their own proof, so the decision is made on both accounts rather than one. If they never delivered anything and never reply, you are refunded in full automatically when that window closes.',
        },
        {
          type: 'p',
          text: 'A review is resolved by releasing payment to the seller, refunding you, or splitting it between you. It is meant for real disagreements, not a first step. Messaging the seller directly resolves most issues faster, and if you sort it out that way you can withdraw the review yourself and the order carries on as normal.',
        },
        {
          type: 'note',
          text: 'When you open a review, Write with AI can draft a starting structure for explaining what happened, with bracketed placeholders for your own specifics. It never invents facts you have not given it.',
        },
      ],
    },
    {
      slug: 'cancelling-an-order',
      title: 'Cancelling an order',
      summary: 'You can back out before the seller signs, and either side can cancel an active order.',
      body: [
        {
          type: 'p',
          text: 'If you have requested a service agreement but the seller has not accepted it yet, either of you can cancel the request with no money involved, since nothing has been charged at that point.',
        },
        {
          type: 'p',
          text: 'An unpaid order also expires on its own after 7 days without a new signature or payment. Whoever it is waiting on gets a reminder 2 days before. Nothing is charged, and you can message the seller to start a fresh one.',
        },
        {
          type: 'p',
          text: 'While an order is active and nothing has been delivered yet, either you or the seller can cancel it from the Orders page, and you are refunded in full.',
        },
        {
          type: 'p',
          text: 'Once the seller has delivered (or shipped) the work, you can no longer cancel it for a refund on your own. Request a revision, or use "Something still not right?" on the order to open a review. An admin looks at both sides and can refund you, release payment, or split it. The seller can still cancel and refund you at any point.',
        },
      ],
    },
  ],
};

export default buying;
